import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import LegalStrip from "@/components/legal/LegalStrip";
import AppCard from "@/components/AppCard";
import { APPS } from "@/lib/apps";
import StoreDemoPreview from "@/components/store/StoreDemoPreview";
import { ShoppingBag } from "lucide-react";

const Index = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div
            className="absolute inset-x-0 top-0 h-1 bg-wavlynq-gradient"
            aria-hidden
          />
          <div className="mx-auto max-w-6xl px-6 pt-24 pb-20 md:pt-32 md:pb-28 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
              The Wavlynq Network — 18+
            </p>
            <h1 className="mt-6 text-5xl md:text-7xl font-bold tracking-tight leading-[1.05]">
              You define the signal.
              <br />
              <span className="bg-wavlynq-gradient bg-clip-text text-transparent">
                We connect it.
              </span>
            </h1>
            <p className="mx-auto mt-8 max-w-2xl text-lg text-muted-foreground leading-relaxed">
              Wavlynq is the home of culturally-grounded community apps —
              with safety, mentorship, and authenticity at the center.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <a
                href="#apps"
                className="inline-flex items-center justify-center rounded-full bg-foreground text-background px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Explore the apps
              </a>
              <Link
                to="/store"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary bg-primary/10 text-primary px-6 py-3 text-sm font-medium hover:bg-primary/20 transition-colors"
              >
                <ShoppingBag className="h-4 w-4" />
                Wavlynq Store
              </Link>
              <Link
                to="/safety"
                className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-secondary transition-colors"
              >
                Safety first
              </Link>
            </div>
          </div>
        </section>

        {/* Store Demo Preview */}
        <StoreDemoPreview />

        {/* Apps grid */}
        <section id="apps" className="border-t border-border bg-secondary/30">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
            <div className="max-w-2xl">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                The communities
              </p>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
                Each app, its own world.
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Every Wavlynq app is built from the ground up around the
                community it serves — its culture, its language, its rhythms.
                Discovery, mentorship, events, and RAELA all show up
                differently inside each one.
              </p>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {APPS.map((app) => (
                <AppCard key={app.id} app={app} />
              ))}
            </div>

            <p className="mt-10 text-sm text-muted-foreground">
              Want access to all nine? The <span className="text-foreground font-medium">Attuned</span> tier
              is the only cross-community membership.
            </p>
          </div>
        </section>

        {/* Pillars strip */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-16 grid gap-10 md:grid-cols-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Safety
              </p>
              <p className="mt-3 text-lg font-semibold">Built in, not bolted on.</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Emergency Button, Crisis Resources, ID verification, and
                crisis-aware RAELA — across every app, every tier.
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Mentorship
              </p>
              <p className="mt-3 text-lg font-semibold">A guide in every app.</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Verified mentors with background checks. Peer guidance that
                actually understands the community you're in.
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                RAELA
              </p>
              <p className="mt-3 text-lg font-semibold">Your in-app companion.</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                A community-aware AI assistant — always-on, crisis-trained,
                culturally tuned to each app.
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-6 pb-12">
          <LegalStrip />
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Index;
